import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Collapse,
  Paper,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Receipt as FacturaIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  PendingActions as PendingIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';

const PanelFacturacion = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data
  const [estadisticas, setEstadisticas] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [facturados, setFacturados] = useState([]);
  const [clientes, setClientes] = useState([]);

  // Tab
  const [tab, setTab] = useState(searchParams.get('estado') === 'facturados' ? 1 : 0);

  // Filtros
  const [filtros, setFiltros] = useState({
    awb: searchParams.get('awb') || '',
    cliente: searchParams.get('cliente') || '',
    desde: searchParams.get('desde') || '',
    hasta: searchParams.get('hasta') || '',
  });

  // Modal Facturar
  const [showFacturarModal, setShowFacturarModal] = useState(false);
  const [vueloAFacturar, setVueloAFacturar] = useState(null);
  const [numeroFactura, setNumeroFactura] = useState('');
  const [saving, setSaving] = useState(false);

  // Modal Editar
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [vueloAEditar, setVueloAEditar] = useState(null);
  const [editData, setEditData] = useState({ cliente: '', factura: '' });

  // Expansión de secciones
  const [expanded, setExpanded] = useState({ pendientes: true, facturados: true });

  useEffect(() => {
    fetchData();
    fetchClientes();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.awb) params.append('awb', filtros.awb);
      if (filtros.cliente) params.append('cliente', filtros.cliente);
      if (filtros.desde) params.append('desde', filtros.desde);
      if (filtros.hasta) params.append('hasta', filtros.hasta);

      const res = await api.get(`${endpoints.facturacion}?${params.toString()}`);
      setEstadisticas(res.data.data.estadisticas);
      setPendientes(res.data.data.pendientes || []);
      setFacturados(res.data.data.facturados || []);
    } catch (error) {
      console.error('Error:', error);
      setError('Error al cargar datos de facturación');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientes = async () => {
    try {
      const res = await api.get(endpoints.oficina.clientesfact);
      setClientes(res.data.data || []);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  const handleFiltrar = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
    fetchData();
  };

  const handleLimpiar = () => {
    setFiltros({ awb: '', cliente: '', desde: '', hasta: '' });
    setSearchParams({});
    fetchData();
  };

  const handleOpenFacturar = (vuelo) => {
    setVueloAFacturar(vuelo);
    setNumeroFactura('');
    setShowFacturarModal(true);
  };

  const handleFacturar = async () => {
    if (!numeroFactura.trim()) {
      setError('Ingrese el número de factura');
      return;
    }

    try {
      setSaving(true);
      await api.post(endpoints.facturacion, {
        awb: vueloAFacturar.awb,
        factura: numeroFactura.trim(),
      });
      setSuccess(`Vuelo ${vueloAFacturar.awb} facturado correctamente como ${numeroFactura}`);
      setShowFacturarModal(false);
      fetchData();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al facturar');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditar = (vuelo) => {
    setVueloAEditar(vuelo);
    setEditData({
      cliente: vuelo.cliente || '',
      factura: vuelo.fact || '',
    });
    setShowEditarModal(true);
  };

  const handleEditar = async () => {
    try {
      setSaving(true);
      await api.put(endpoints.facturacion, {
        awb: vueloAEditar.awb,
        cliente: editData.cliente,
        factura: editData.factura,
      });
      setSuccess('Vuelo actualizado correctamente');
      setShowEditarModal(false);
      fetchData();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const formatNumber = (num) => {
    return (num || 0).toLocaleString();
  };

  const getDiferenciaColor = (diff) => {
    const abs = Math.abs(diff);
    if (abs <= 50) return 'success';
    if (diff > 0) return 'warning';
    return 'error';
  };

  const StatCard = ({ value, label, color }) => (
    <Card sx={{ borderLeft: 4, borderColor: `${color}.main` }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  const VuelosTable = ({ vuelos, isPendiente }) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: isPendiente ? 'warning.50' : 'success.50' }}>
            <TableCell>AWB</TableCell>
            <TableCell>Destino</TableCell>
            <TableCell>Fecha/Cierre</TableCell>
            <TableCell>Cliente</TableCell>
            <TableCell align="right">Pallets</TableCell>
            <TableCell align="right">Kilos Bruto</TableCell>
            <TableCell align="right">Kilos Fact.</TableCell>
            <TableCell align="right">Kilos Manif.</TableCell>
            <TableCell align="center">Diferencia</TableCell>
            {!isPendiente && <TableCell>N° Factura</TableCell>}
            <TableCell align="center">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vuelos.map((v) => (
            <TableRow key={v.id} hover>
              <TableCell>
                <Typography fontWeight="bold">{v.awb}</Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(v.destino || '').split(';').slice(0, 3).map((d, i) => (
                    <Chip key={i} label={d} size="small" variant="outlined" />
                  ))}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {new Date(v.fecha).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {v.fcierre ? new Date(v.fcierre).toLocaleString() : '-'}
                </Typography>
              </TableCell>
              <TableCell>{v.cliente || '-'}</TableCell>
              <TableCell align="right">{v.tpallets || 0}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatNumber(v.kilos_bruto)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatNumber(v.kilos_facturables)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {formatNumber(v.kilos_manifiesto)}
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={`${v.diferencia >= 0 ? '+' : ''}${formatNumber(v.diferencia)}`}
                  size="small"
                  color={getDiferenciaColor(v.diferencia)}
                />
              </TableCell>
              {!isPendiente && (
                <TableCell>
                  <Typography fontWeight="bold" color="success.main">
                    {v.fact}
                  </Typography>
                </TableCell>
              )}
              <TableCell align="center">
                {isPendiente && (
                  <Tooltip title="Facturar">
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => handleOpenFacturar(v)}
                      sx={{ minWidth: 'auto', px: 2 }}
                    >
                      Facturar
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Editar">
                  <IconButton size="small" color="primary" onClick={() => handleOpenEditar(v)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {vuelos.length === 0 && (
            <TableRow>
              <TableCell colSpan={isPendiente ? 10 : 11} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No hay vuelos</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #7B1FA2 0%, #9C27B0 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FacturaIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Panel de Facturación
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>
                Pallet Control | Facturación de vuelos cerrados
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <form onSubmit={handleFiltrar}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="AWB"
                  value={filtros.awb}
                  onChange={(e) => setFiltros({ ...filtros, awb: e.target.value })}
                  fullWidth
                  size="small"
                  placeholder="Ej: U712345678"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Cliente"
                  value={filtros.cliente}
                  onChange={(e) => setFiltros({ ...filtros, cliente: e.target.value })}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Desde"
                  type="date"
                  value={filtros.desde}
                  onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Hasta"
                  type="date"
                  value={filtros.hasta}
                  onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
                    Filtrar
                  </Button>
                  <Button variant="outlined" onClick={handleLimpiar} startIcon={<ClearIcon />}>
                    Limpiar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Estadísticas */}
      {estadisticas && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={2.4}>
            <StatCard
              value={estadisticas.pendientes}
              label="Pendientes"
              color="warning"
            />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <StatCard
              value={estadisticas.facturados}
              label="Facturados"
              color="success"
            />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <StatCard
              value={parseInt(estadisticas.pendientes) + parseInt(estadisticas.facturados)}
              label="Total Vuelos"
              color="primary"
            />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <StatCard
              value={formatNumber(estadisticas.total_pallets)}
              label="Total Pallets"
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard
              value={formatNumber(estadisticas.kilos_facturables)}
              label="Kilos Facturables"
              color="info"
            />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab
            icon={<PendingIcon />}
            iconPosition="start"
            label={`Pendientes (${pendientes.length})`}
          />
          <Tab
            icon={<CheckIcon />}
            iconPosition="start"
            label={`Facturados (${facturados.length})`}
          />
        </Tabs>
      </Paper>

      {/* Contenido Tab */}
      {tab === 0 && (
        <Card sx={{ borderTop: 4, borderColor: 'warning.main' }}>
          <CardContent sx={{ pb: 0 }}>
            <Typography variant="h6" fontWeight="bold" color="warning.dark">
              Pendientes de Facturar ({pendientes.length})
            </Typography>
          </CardContent>
          <VuelosTable vuelos={pendientes} isPendiente={true} />
        </Card>
      )}

      {tab === 1 && (
        <Card sx={{ borderTop: 4, borderColor: 'success.main' }}>
          <CardContent sx={{ pb: 0 }}>
            <Typography variant="h6" fontWeight="bold" color="success.dark">
              Ya Facturados ({facturados.length})
            </Typography>
          </CardContent>
          <VuelosTable vuelos={facturados} isPendiente={false} />
        </Card>
      )}

      {/* Volver */}
      <Box sx={{ mt: 4 }}>
        <Button component={Link} to="/facturacion" variant="contained" color="inherit" startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>

      {/* Modal Facturar */}
      <Dialog open={showFacturarModal} onClose={() => setShowFacturarModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Facturar Vuelo</DialogTitle>
        <DialogContent>
          {vueloAFacturar && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                AWB: <strong>{vueloAFacturar.awb}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cliente: <strong>{vueloAFacturar.cliente}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Kilos Facturables: <strong>{formatNumber(vueloAFacturar.kilos_facturables)}</strong>
              </Typography>
            </Box>
          )}
          <TextField
            label="Número de Factura"
            value={numeroFactura}
            onChange={(e) => setNumeroFactura(e.target.value)}
            fullWidth
            autoFocus
            placeholder="Ej: FAC-2025-00123"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFacturarModal(false)}>Cancelar</Button>
          <Button onClick={handleFacturar} variant="contained" color="success" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Facturar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Editar */}
      <Dialog open={showEditarModal} onClose={() => setShowEditarModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Vuelo</DialogTitle>
        <DialogContent>
          {vueloAEditar && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6">
                AWB: <strong>{vueloAEditar.awb}</strong>
              </Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={editData.cliente}
                  onChange={(e) => setEditData({ ...editData, cliente: e.target.value })}
                  label="Cliente"
                >
                  <MenuItem value="">-- Seleccionar --</MenuItem>
                  {clientes.map((c) => (
                    <MenuItem key={c.id} value={c.nombre}>
                      {c.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Número de Factura"
                value={editData.factura}
                onChange={(e) => setEditData({ ...editData, factura: e.target.value })}
                fullWidth
                placeholder="Ej: FAC-2025-00123"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditarModal(false)}>Cancelar</Button>
          <Button onClick={handleEditar} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PanelFacturacion;




